/*
 * BERTINDAK ATAS NAMA — K140, 1 September 2026.
 *
 * ⛔ KEPALA BERKAS INI DULU BERBUNYI "DEV ONLY — WAJIB DIHAPUS SEBELUM
 *    PRODUCTION". Itu DICABUT K140: fitur ini NAIK PANGKAT jadi permanen
 *    berpagar, dan §16a sudah dikoreksi Owner sendiri.
 *
 *    Alasannya terukur: FINANCE, DESIGNER_EDITOR, dan SPV_DESAINER
 *    masing-masing dipegang SATU akun. Satu orang cuti berarti satu alur
 *    berhenti — dan "pembayaran vendor murni hak Finance" berarti Finance
 *    cuti berarti uang keluar berhenti total.
 *
 * ---------------------------------------------------------------------
 * ⛔ BENTUKNYA DIUBAH 2 September 2026 — DAN SEBABNYA KELUHAN OWNER
 * ---------------------------------------------------------------------
 *
 * Versi pertama menaruh kolom alasan PERMANEN DI HEADER: terlihat sepanjang
 * hari, tiap halaman, bahkan saat orangnya nol berniat mendelegasi apa pun.
 * Owner membacanya persis seperti apa yang ia jadi: *"seakan-akan sistem
 * tidak percaya dengan Owner dan Kepala Cabang"*.
 *
 * Kekhawatiran yang melahirkan bentuk itu benar — alasan yang diminta
 * SESUDAH peran dipilih terasa seperti hadangan. Tapi obatnya salah: satu
 * hadangan di tengah ditukar dengan satu tuntutan yang selalu menatap.
 *
 * ⚠ Yang BUKAN ketidakpercayaan: `alasan` tetap WAJIB (K140 butir 6). Tiga
 *   bulan lagi, saat ada yang bertanya kenapa Owner membayar vendor atas
 *   nama Finance pada 3 September, catatan itu yang menjawab — bukan ingatan
 *   Owner. Ia berdiri di pihak yang mendelegasi.
 *
 * Bentuk sekarang mengikuti pola Odoo "Secure Impersonation": satu tombol
 * ringkas, lalu SATU dialog yang menanyakan peran + region + alasan
 * sekaligus. Sesudah dialog ditutup, nol ada yang menatap.
 *
 * ⛔ KOMPONEN INI BUKAN PAGAR KEAMANAN, dan itu nol berubah. Pagarnya di
 *    backend: `delegasi_pagar.boleh_meminjam()` diperiksa saat token terbit
 *    DAN diulang tiap permintaan.
 *
 * ⚠ Nama berkas dibiarkan `DevRoleSwitcher.jsx` supaya diff-nya kecil dan
 *   riwayatnya terbaca. Ganti nama adalah unit kerja tersendiri.
 */
import React, { useCallback, useEffect, useState } from "react";
import { UserCog, RotateCcw } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { REGION_UNBOUND_ROLES } from "../../lib/roles";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

/*
 * ⛔ CERMIN `delegasi_pagar.py`. Kalau daftar di backend berubah, daftar ini
 *    WAJIB ikut — tapi yang menegakkan tetap backend. Layar yang menyembunyikan
 *    tombol nol dianggap keamanan (CLAUDE.md).
 */
const PERAN_KC_BOLEH = ["CS", "ADMIN_PRODUKSI", "ADMIN_GUDANG"];

/*
 * T14/T15 (Titipan auditor 26 Okt 2026) — cermin `dev_impersonation.PERAN_TANPA_CAKUPAN` /
 * `PERAN_TERIKAT_CABANG`. Sesi atas-nama dibuat PERSIS seperti akun asli jabatan itu:
 *   - Desainer & SPV Desainer: tanpa wilayah, tanpa cabang (akunnya NULL/NULL, K56 poin 3);
 *   - CS, Admin Produksi, Admin Gudang: wilayah + CABANG (tanpa cabang, layar PD/FA menolak dan
 *     Buat WO memilih cabang pertama diam-diam).
 */
export const PERAN_TANPA_CAKUPAN = ["DESIGNER_EDITOR", "SPV_DESAINER"];
export const PERAN_TERIKAT_CABANG = ["CS", "ADMIN_PRODUKSI", "ADMIN_GUDANG"];

/* Nol seorang pun boleh meminjam OWNER — pagar yang menjaga pagar lain. */
// K236: PEMANTAU (hanya baca) juga tidak bisa dipinjam.
const NOL_BOLEH_DIPINJAM = ["OWNER", "PEMANTAU"];

/*
 * ⛔ ALASAN JADI PILIHAN, bukan ketikan bebas — permintaan Owner 2 Sep 2026.
 *
 *    Untungnya bukan cuma kecepatan: alasan yang diketik bebas melahirkan
 *    "test" delapan kali (terukur — 25 dari sesi hari pertama nol punya
 *    alasan sama sekali, sisanya sebagian besar teks uji). Pilihan yang
 *    terbatas bisa dihitung di laporan tinjauan; kalimat bebas tidak.
 *
 * ⛔ "Lainnya" WAJIB ADA, dan ia bukan pelengkap. Daftar tertutup memaksa
 *    orang memilih yang PALING MIRIP saat sebabnya nol ada di daftar — dan
 *    catatan yang HAMPIR benar lebih berbahaya daripada catatan kosong,
 *    karena ia terbaca meyakinkan dan nol ada yang memeriksanya lagi.
 */
const ALASAN_PILIHAN = [
  "Pemegang jabatan cuti",
  "Pemegang jabatan sakit",
  "Pemegang jabatan sedang di luar kantor",
  "Jabatan ini sedang kosong",
  "Pekerjaan mendesak, pemegang jabatan tidak dapat dihubungi",
  "Menguji tampilan jabatan ini",
];
const ALASAN_LAIN = "Lainnya";
const CATATAN_MIN = 8;

export default function DevRoleSwitcher() {
  const {
    delegasiAktif, impersonation, profile, peranAsli: peranAkun,
    startImpersonation, stopImpersonation,
  } = useAuth();

  const [buka, setBuka] = useState(false);
  const [roles, setRoles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [stores, setStores] = useState([]);
  const [peran, setPeran] = useState("");
  const [region, setRegion] = useState("");
  const [cabang, setCabang] = useState("");
  const [alasan, setAlasan] = useState("");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Peran ASLI, bukan peran efektif. Saat bertindak atas nama Finance,
  // `isOwner` dari context bernilai false — kalau dipakai di sini, kendalinya
  // menghilang persis ketika paling dibutuhkan untuk kembali.
  // K236: `peranAkun` = peran ASLI dari context (Pemantau tampil sebagai Owner di layar baca, tetapi tidak
  // pernah boleh mendelegasi).
  const peranAsli = impersonation
    ? impersonation.real_role_code
    : peranAkun;

  const bolehMendelegasi = peranAsli === "OWNER" || peranAsli === "KEPALA_CABANG";
  const tampil = delegasiAktif && bolehMendelegasi;

  useEffect(() => {
    if (!tampil || !buka) return;
    let batal = false;
    Promise.all([api.get("/roles"), api.get("/regions"), api.get("/stores")])
      .then(([r1, r2, r3]) => {
        if (batal) return;
        setRoles(r1.data || []);
        setRegions(r2.data || []);
        setStores(Array.isArray(r3.data) ? r3.data : []);
      })
      .catch(() => { /* daftar kosong = dropdown kosong, bukan layar rusak */ });
    return () => { batal = true; };
  }, [tampil, buka]);

  const butuhRegion = !!peran && !REGION_UNBOUND_ROLES.includes(peran)
    && !PERAN_TANPA_CAKUPAN.includes(peran);
  const butuhCabang = butuhRegion && PERAN_TERIKAT_CABANG.includes(peran);
  const cabangDiWilayah = stores.filter((c) => c.region_id === region);
  const perluCatatan = alasan === ALASAN_LAIN;
  const alasanFinal = perluCatatan ? catatan.trim() : alasan;

  const siap =
    !!peran &&
    (!butuhRegion || !!region) &&
    (!butuhCabang || !!cabang) &&
    !!alasan &&
    (!perluCatatan || catatan.trim().length >= CATATAN_MIN);

  const terapkan = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      await startImpersonation(
        peran, butuhRegion ? region : null, alasanFinal, butuhCabang ? cabang : null,
      );
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || "Gagal bertindak atas nama.");
      setBusy(false);
    }
  }, [startImpersonation, peran, region, cabang, butuhRegion, butuhCabang, alasanFinal]);

  if (!tampil) return null;

  /*
   * Kepala Cabang: tiga peran itu saja, dan hanya regionnya sendiri.
   * Menampilkan pilihan yang backend tolak adalah cara paling rapi membuat
   * orang mengira sistemnya rusak.
   */
  const peranTerpilih = roles.filter((r) => {
    if (NOL_BOLEH_DIPINJAM.includes(r.code)) return false;
    if (peranAsli === "KEPALA_CABANG") return PERAN_KC_BOLEH.includes(r.code);
    return true;
  });
  const regionTerpilih = peranAsli === "KEPALA_CABANG"
    ? regions.filter((r) => r.id === profile?.region_id)
    : regions;

  // --- sedang bertindak atas nama: badge + tombol kembali, nol lagi ---
  if (impersonation) {
    return (
      <div className="flex items-center gap-2" data-testid="dev-role-switcher">
        <Badge
          data-testid="dev-impersonation-badge"
          className="bg-warning text-warning-foreground"
          title={`Jabatan asli: ${impersonation.real_role_name || impersonation.real_role_code} (${impersonation.real_email}) · berakhir ${impersonation.expires_at}`}
        >
          <UserCog size={13} className="mr-1" />
          ATAS NAMA: {impersonation.as_role_name}
          {impersonation.as_region_name ? ` · ${impersonation.as_region_name}` : ""}
          {impersonation.as_store_name ? ` · ${impersonation.as_store_name}` : ""}
        </Badge>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={stopImpersonation}
          data-testid="dev-stop-impersonation"
        >
          <RotateCcw size={13} className="mr-1" />
          Kembali jadi diri sendiri
        </Button>
      </div>
    );
  }

  // --- belum: SATU tombol ringkas ---
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-8 text-xs"
        onClick={() => setBuka(true)}
        data-testid="dev-role-switcher"
      >
        <UserCog size={13} className="mr-1" />
        Log in as…
      </Button>

      <Dialog open={buka} onOpenChange={(o) => { setBuka(o); if (!o) setErr(null); }}>
        <DialogContent className="max-w-md" data-testid="delegasi-dialog">
          <DialogHeader>
            <DialogTitle>Bertindak Atas Nama Jabatan Lain</DialogTitle>
            <DialogDescription>
              Wewenangnya yang dipinjam, bukan identitasmu. Setiap tindakan
              tetap tercatat atas namamu, dan sesi ini berakhir sendiri dalam
              8 jam.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Jabatan</Label>
              <Select value={peran} onValueChange={(v) => { setPeran(v); setRegion(""); setCabang(""); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="dev-role-select">
                  <SelectValue placeholder="Pilih jabatan..." />
                </SelectTrigger>
                <SelectContent>
                  {peranTerpilih.map((r) => (
                    <SelectItem key={r.id} value={r.code}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Muncul HANYA untuk peran yang terikat region — peran lintas
                region nol punya region, dan menanyakannya cuma membingungkan. */}
            {butuhRegion && (
              <div className="space-y-1.5">
                <Label className="text-xs">Wilayah</Label>
                <Select value={region} onValueChange={(v) => { setRegion(v); setCabang(""); }}>
                  <SelectTrigger className="h-9 text-sm" data-testid="dev-region-select">
                    <SelectValue placeholder="Pilih wilayah..." />
                  </SelectTrigger>
                  <SelectContent>
                    {regionTerpilih.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* T15: jabatan terikat cabang membawa CABANG — sama dengan akun aslinya. */}
            {butuhCabang && (
              <div className="space-y-1.5">
                <Label className="text-xs">Cabang</Label>
                <Select value={cabang} onValueChange={setCabang} disabled={!region}>
                  <SelectTrigger className="h-9 text-sm" data-testid="dev-store-select">
                    <SelectValue placeholder={region ? "Pilih cabang..." : "Pilih wilayah dulu"} />
                  </SelectTrigger>
                  <SelectContent>
                    {cabangDiWilayah.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Alasan</Label>
              <Select value={alasan} onValueChange={setAlasan}>
                <SelectTrigger className="h-9 text-sm" data-testid="delegasi-alasan">
                  <SelectValue placeholder="Pilih alasan..." />
                </SelectTrigger>
                <SelectContent>
                  {ALASAN_PILIHAN.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                  <SelectItem value={ALASAN_LAIN}>{ALASAN_LAIN}…</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {perluCatatan && (
              <Input
                data-testid="delegasi-alasan-lain"
                value={catatan}
                onChange={(e) => setCatatan(e.target.value)}
                placeholder={`Tulis alasannya (min. ${CATATAN_MIN} karakter)`}
                className="h-9 text-sm"
                autoFocus
              />
            )}

            {err && (
              <p className="text-xs text-danger-soft-foreground" data-testid="dev-role-switcher-error">{err}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setBuka(false)} disabled={busy}>
              Batal
            </Button>
            <Button
              size="sm"
              onClick={terapkan}
              disabled={!siap || busy}
              data-testid="delegasi-terapkan"
            >
              {busy ? "Menerapkan..." : "Terapkan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
